import Swal from "sweetalert2";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import { obtenerDatosConjunto } from "../../functions/consultas";
import { actualizarMultiple } from "../../functions/crud";
import {
  parsearFechaPase,
  fechaHoraDart,
  obtenerYearMes,
  rutaPase,
} from "../../functions/pase_visitante";
import {
  tarifaDesdeConjunto,
  calcularCobro,
  ejemplosCobro,
  formatearDuracion,
} from "../../functions/cobro_parqueadero";
import logoCoordiPH from "../../assets/coordi_ph_logo_letras_2.png";

const formatearMoneda = (valor) =>
  new Intl.NumberFormat("es-CO", {
    style: "currency",
    currency: "COP",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(valor || 0);

const parsearFecha = parsearFechaPase;

const formatearFecha = (fecha) => {
  if (!fecha) return "No registrado";
  const dd = String(fecha.getDate()).padStart(2, "0");
  const mm = String(fecha.getMonth() + 1).padStart(2, "0");
  const yyyy = fecha.getFullYear();
  const hh = String(fecha.getHours()).padStart(2, "0");
  const mi = String(fecha.getMinutes()).padStart(2, "0");
  const ss = String(fecha.getSeconds()).padStart(2, "0");
  return `${dd}/${mm}/${yyyy} ${hh}:${mi}:${ss}`;
};

const esMoto = (visitante) => {
  const tipo = (visitante?.tipoVehiculo || "").toUpperCase();
  return tipo === "MOTO" || tipo === "MOTOCICLETA";
};

const formatearTiempo = (minutosTotales) => {
  const horas = Math.floor(minutosTotales / 60);
  const minutos = minutosTotales % 60;
  if (horas <= 0 && minutos <= 0) return "0 minutos";
  const partes = [];
  if (horas > 0) partes.push(`${horas} hora${horas === 1 ? "" : "s"}`);
  if (minutos > 0) partes.push(`${minutos} minuto${minutos === 1 ? "" : "s"}`);
  return partes.join(" ");
};

const calcularFactura = (visitante, conjunto) => {
  const fechaEntrada =
    parsearFecha(visitante?.fechaHoraIngresoISO) ||
    parsearFecha(visitante?.fechaHoraIngreso) ||
    parsearFecha(visitante?.fechaCreacionRegistroISO) ||
    parsearFecha(visitante?.fechaCreacionRegistro);
  const fechaSalida =
    parsearFecha(visitante?.fechaHoraSalidaISO) ||
    parsearFecha(visitante?.fechaHoraSalida);

  let minutos = 0;
  if (fechaEntrada) {
    const salida = fechaSalida || new Date();
    const diff = salida.getTime() - fechaEntrada.getTime();
    minutos = diff > 0 ? Math.floor(diff / 60000) : 0;
  }

  const tarifa = tarifaDesdeConjunto(conjunto, esMoto(visitante));
  const cobro = calcularCobro(tarifa, minutos);

  return {
    ...tarifa,
    ...cobro,
    fechaEntrada,
    fechaSalida,
    minutos,
    valorAPagar: cobro.total,
  };
};

const lineasCobro = (calculo) =>
  calculo.minutosCobrables === 0
    ? [["Cobro:", "Ninguno (tiempo gratuito)"]]
    : [
        ["Franjas:", `${calculo.franjasCompletas} × ${formatearMoneda(calculo.valorFranja)}`],
        ["Horas adicionales:", `${calculo.horasSobrantes} → ${formatearMoneda(calculo.valorHorasSobrantes)}`],
      ];

const escapar = (texto) =>
  String(texto ?? "").replace(/[&<>"']/g, (c) => ({
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#39;",
  }[c]));

const fila = (etiqueta, valor, resaltado = false) => {
  const tamano = resaltado ? "16px" : "14px";
  const texto = valor !== null && valor !== undefined && `${valor}` !== ""
    ? valor
    : "No registrado";
  return `
    <div style="display:flex;justify-content:space-between;gap:12px;padding:3px 0;">
      <span style="font-size:${tamano};font-weight:bold;color:#000;">${escapar(etiqueta)}</span>
      <span style="font-size:${tamano};font-weight:${resaltado ? "bold" : "normal"};color:#000;text-align:right;">${escapar(texto)}</span>
    </div>`;
};

// Ejemplos con la tarifa del vehículo del visitante, para que entienda el cobro.
const filasEjemplos = (visitante, calculo) =>
  ejemplosCobro(calculo.minutosGratis, calculo.minutosFranja).map((ejemplo) => [
    ejemplo.descripcion,
    formatearMoneda(calcularCobro(calculo, ejemplo.minutos).total),
  ]);

const tablaEjemplosHTML = (visitante, calculo) => {
  const celda = "border:1px solid #bdbdbd;padding:6px 8px;font-size:13px;color:#000;";
  return `
      <div style="height:16px;"></div>
      <div style="text-align:center;font-size:14px;font-weight:bold;color:#000;">¿Cómo se cobra?</div>
      <div style="height:8px;"></div>
      <table style="width:100%;border-collapse:collapse;">
        <tr style="background:#eee;">
          <th style="${celda}text-align:left;">Descripción</th>
          <th style="${celda}text-align:right;">${esMoto(visitante) ? "Moto" : "Carro"}</th>
        </tr>
        ${filasEjemplos(visitante, calculo)
          .map(
            ([descripcion, valor]) => `
        <tr>
          <td style="${celda}text-align:left;">${escapar(descripcion)}</td>
          <td style="${celda}text-align:right;white-space:nowrap;">${escapar(valor)}</td>
        </tr>`
          )
          .join("")}
      </table>`;
};

const generarTicketHTML = (visitante, conjunto, calculo, notificacion) => {
  const { telefono, fecha } = notificacion || {};

  return `
    <div id="ticket-factura" style="background:#fff;padding:16px;text-align:left;">
      <div style="text-align:center;">
        <img src="${logoCoordiPH}" alt="CoordiPH" style="height:50px;object-fit:contain;" />
      </div>
      <div style="height:12px;"></div>
      <div style="text-align:center;font-size:18px;font-weight:bold;color:#000;">
        ${escapar((conjunto?.Nombre || "").toUpperCase())}
      </div>
      <div style="height:4px;"></div>
      <div style="text-align:center;font-size:13px;color:#000;">
        ${escapar(conjunto?.Direccion || "")}
      </div>
      <div style="height:8px;"></div>
      <div style="text-align:center;font-size:11px;font-style:italic;color:#000;">
        ¡Esto no es una factura electrónica!
      </div>
      <div style="height:16px;"></div>

      ${fila("Inmueble:", visitante?.casa)}
      ${fila("Tiempo gratuito:", formatearDuracion(calculo.minutosGratis))}
      ${fila("Duración franja:", formatearDuracion(calculo.minutosFranja))}
      ${fila("Vigilante:", visitante?.autorizaSalida || "PORTERÍA")}
      ${fila("Placa:", visitante?.placasVehiculo)}
      ${fila("Tipo Vehículo:", (visitante?.tipoVehiculo || "").toUpperCase())}
      ${fila("Valor por hora:", formatearMoneda(calculo.valorHora ?? 0))}
      ${fila("Valor franja:", formatearMoneda(calculo.valorFranja))}
      ${fila("Fecha entrada:", formatearFecha(calculo.fechaEntrada))}
      ${fila("Fecha salida:", formatearFecha(calculo.fechaSalida))}
      ${fila("Tiempo:", formatearTiempo(calculo.minutos))}
      ${lineasCobro(calculo).map(([etiqueta, valor]) => fila(etiqueta, valor)).join("")}

      <div style="height:8px;"></div>
      <hr style="border:none;border-top:1px solid #ddd;margin:0;" />

      ${fila("Valor a pagar:", formatearMoneda(calculo.valorAPagar))}
      ${fila("Impuesto:", formatearMoneda(0))}
      ${fila("Total a pagar:", formatearMoneda(calculo.valorAPagar), true)}

      <div style="height:8px;"></div>
      <hr style="border:none;border-top:1px solid #ddd;margin:0;" />

      ${
        telefono
          ? `
      <div style="height:4px;"></div>
      <div style="text-align:center;font-size:12px;font-weight:bold;color:#1b5e20;">
        Información enviada al teléfono
      </div>
      <div style="height:2px;"></div>
      <div style="text-align:center;font-size:13px;color:#000;">${escapar(telefono)}</div>
      ${
        fecha
          ? `<div style="text-align:center;font-size:11px;color:#000;">${escapar(
              fecha.length >= 19 ? fecha.substring(0, 19) : fecha
            )}</div>`
          : ""
      }`
          : ""
      }

      <div style="height:8px;"></div>
      <div style="text-align:center;font-size:10px;font-style:italic;color:#000;">
        Nuestra responsabilidad está limitada a la custodia del vehículo.
      </div>
      ${tablaEjemplosHTML(visitante, calculo)}
    </div>`;
};

const generarTextoFactura = (visitante, conjunto, calculo) =>
  [
    `*${(conjunto?.Nombre || "").toUpperCase()}*`,
    conjunto?.Direccion || "",
    "¡Esto no es una factura electrónica!",
    "",
    `Inmueble: ${visitante?.casa || "No registrado"}`,
    `Tiempo gratuito: ${formatearDuracion(calculo.minutosGratis)}`,
    `Duración franja: ${formatearDuracion(calculo.minutosFranja)}`,
    `Vigilante: ${visitante?.autorizaSalida || "PORTERÍA"}`,
    `Placa: ${visitante?.placasVehiculo || "No registrado"}`,
    `Tipo Vehículo: ${(visitante?.tipoVehiculo || "No registrado").toUpperCase()}`,
    `Valor por hora: ${formatearMoneda(calculo.valorHora ?? 0)}`,
    `Valor franja: ${formatearMoneda(calculo.valorFranja)}`,
    `Fecha entrada: ${formatearFecha(calculo.fechaEntrada)}`,
    `Fecha salida: ${formatearFecha(calculo.fechaSalida)}`,
    `Tiempo: ${formatearTiempo(calculo.minutos)}`,
    ...lineasCobro(calculo).map(([etiqueta, valor]) => `${etiqueta} ${valor}`),
    "",
    `*Total a pagar: ${formatearMoneda(calculo.valorAPagar)}*`,
    "",
    "Nuestra responsabilidad está limitada a la custodia del vehículo.",
    "",
    "*¿Cómo se cobra?*",
    ...filasEjemplos(visitante, calculo).map(([descripcion, valor]) => `${descripcion}: ${valor}`),
  ]
    .filter((linea) => linea !== null && linea !== undefined)
    .join("\n");

const guardarTelefonoFactura = async (visitante, telefono, fechaNotificacion) => {
  const nit = localStorage.getItem("CodigoCliente") || "900362633";
  const [year, mes] = obtenerYearMes(visitante);
  const ruta = rutaPase(nit, visitante?.tipoPermiso, year, mes, visitante.id);

  await actualizarMultiple({
    [`${ruta}/telefonoFactura`]: telefono,
    [`${ruta}/fechaNotificacionFactura`]: fechaNotificacion,
  });
};

// PDF de una página con la captura del ticket, igual que en la app móvil.
const generarPdfTicket = async (nombreArchivo) => {
  const ticket = document.getElementById("ticket-factura");
  if (!ticket) return null;
  const canvas = await html2canvas(ticket, { scale: 3, backgroundColor: "#ffffff" });
  const pdf = new jsPDF({ unit: "pt", format: "a4" });
  const margen = 24;
  const anchoHoja = pdf.internal.pageSize.getWidth() - margen * 2;
  const altoHoja = pdf.internal.pageSize.getHeight() - margen * 2;
  const escala = Math.min(anchoHoja / canvas.width, altoHoja / canvas.height);
  const ancho = canvas.width * escala;
  const alto = canvas.height * escala;
  pdf.addImage(
    canvas.toDataURL("image/png"),
    "PNG",
    (pdf.internal.pageSize.getWidth() - ancho) / 2,
    margen,
    ancho,
    alto
  );
  return new File([pdf.output("blob")], nombreArchivo, { type: "application/pdf" });
};

const descargarArchivo = (archivo) => {
  const url = URL.createObjectURL(archivo);
  const enlace = document.createElement("a");
  enlace.href = url;
  enlace.download = archivo.name;
  document.body.appendChild(enlace);
  enlace.click();
  enlace.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const imprimirTicket = () => {
  const ticket = document.getElementById("ticket-factura");
  if (!ticket) return;

  const ventana = window.open("", "_blank", "width=420,height=680");
  if (!ventana) {
    Swal.showValidationMessage("Permite las ventanas emergentes para imprimir");
    return;
  }

  ventana.document.write(`
    <html>
      <head><title>Factura de parqueadero</title></head>
      <body style="margin:0;font-family:Arial,Helvetica,sans-serif;">
        ${ticket.outerHTML}
      </body>
    </html>`);
  ventana.document.close();
  ventana.focus();
  setTimeout(() => {
    ventana.print();
    ventana.close();
  }, 400);
};

export const generarFactura = async (visitante) => {
  if (!visitante?.id) {
    Swal.fire("Error", "El registro no tiene un ID válido", "error");
    return false;
  }

  Swal.fire({
    title: "Generando factura",
    didOpen: () => Swal.showLoading(),
    allowOutsideClick: false,
  });

  const conjunto = await obtenerDatosConjunto();
  const calculo = calcularFactura(visitante, conjunto);

  let telefonoNotificado = visitante.telefonoFactura || "";
  let fechaNotificacion = visitante.fechaNotificacionFactura || "";

  let numeroPrevio = telefonoNotificado;
  if (numeroPrevio.startsWith("57") && numeroPrevio.length > 10) {
    numeroPrevio = numeroPrevio.substring(2);
  }

  Swal.close();

  const { isConfirmed, value: envio } = await Swal.fire({
    title: "Generar factura",
    width: 460,
    html: `
      ${generarTicketHTML(visitante, conjunto, calculo, {
        telefono: telefonoNotificado,
        fecha: fechaNotificacion,
      })}
      <div style="text-align:left;padding:0 16px 8px;">
        <label style="display:block;margin-bottom:6px;color:#555;font-size:14px;font-weight:500;">
          Número de WhatsApp
        </label>
        <div style="display:flex;align-items:center;gap:8px;">
          <span style="font-size:14px;color:#555;">+57</span>
          <input id="numeroWhatsapp" class="swal2-input" placeholder="Ej: 3001234567"
            inputmode="numeric" value="${escapar(numeroPrevio)}"
            style="margin:0;width:100%;box-sizing:border-box;padding:10px;border:1px solid #d1d5db;border-radius:5px;font-size:14px;">
        </div>
      </div>
    `,
    showCancelButton: true,
    showDenyButton: true,
    confirmButtonText: "Enviar por WhatsApp",
    denyButtonText: "Imprimir",
    cancelButtonText: "Cerrar",
    denyButtonColor: "#6c757d",
    showLoaderOnConfirm: true,
    preConfirm: async () => {
      const numero = (document.getElementById("numeroWhatsapp")?.value || "").trim();
      if (!numero) {
        Swal.showValidationMessage("Ingrese el número de WhatsApp");
        return false;
      }
      // El PDF se arma y comparte aquí, mientras el ticket sigue en pantalla y
      // el navegador aún considera el clic del usuario (requisito de share).
      const pdf = await generarPdfTicket(`factura_${visitante.id}.pdf`);
      let compartido = false;
      if (pdf && navigator.canShare?.({ files: [pdf] })) {
        try {
          await navigator.share({ files: [pdf], title: "Factura de parqueadero" });
          compartido = true;
        } catch (error) {
          if (error?.name === "AbortError") return false;
        }
      }
      if (pdf && !compartido) descargarArchivo(pdf);
      return { numero: `57${numero.replace(/[^0-9]/g, "")}`, compartido };
    },
    didRender: () => {
      const botonImprimir = Swal.getDenyButton();
      if (botonImprimir) {
        botonImprimir.onclick = (evento) => {
          evento.preventDefault();
          evento.stopPropagation();
          imprimirTicket();
        };
      }
    },
  });

  if (!isConfirmed || !envio) return false;
  const numeroLimpio = envio.numero;

  const fechaEnvio = fechaHoraDart();

  try {
    await guardarTelefonoFactura(visitante, numeroLimpio, fechaEnvio);
    visitante.telefonoFactura = numeroLimpio;
    visitante.fechaNotificacionFactura = fechaEnvio;
    telefonoNotificado = numeroLimpio;
    fechaNotificacion = fechaEnvio;
  } catch (error) {
    console.error("Error guardando teléfono de factura:", error);
    Swal.fire("Error", "No se pudo registrar el envío de la factura", "error");
    return false;
  }

  // Si el navegador no puede compartir archivos, el PDF quedó descargado y se
  // abre el chat para adjuntarlo; el resumen en texto va como respaldo.
  if (!envio.compartido) {
    const mensaje = encodeURIComponent(
      `${generarTextoFactura(visitante, conjunto, calculo)}

(Adjunto la factura en PDF)`
    );
    window.open(`https://wa.me/${numeroLimpio}?text=${mensaje}`, "_blank", "noopener");
  }

  await Swal.fire({
    title: "Información enviada al teléfono",
    text: `${telefonoNotificado} · ${fechaNotificacion.substring(0, 24)}`,
    icon: "success",
    timer: 2500,
    showConfirmButton: false,
  });

  return true;
};
